<script>
  import { ntc } from '../../routes/design/[productSlug]/_nameThatColor.js';
  ntc.init();
  import debounce from 'lodash/debounce';

  import { goto } from '@sapper/app';
  import { onMount } from 'svelte';

  import { productDataStore } from '../../stores/ProductData.js';
  import { designState } from '../../stores/Designer.js';
  import { konvaStageId } from '../../stores/Preview.js';

  import { rgbToHex, hexToRgb } from '../../utils/colourUtils.js';

  import ColourOptionCard from '../../components/Design/ColourOptionCard.svelte';
  import ProductTypeSelectionColour from '../../components/Design/ProductTypeSelectionColour.svelte';

  export let resizeFunction;

  function generateSubColoursObject(selectedColour, numberOfTemplateColours = 4) {
    return [
      {
        slot: 'main',
        hex: rgbToHex(selectedColour.ProductColoursMasterMain?.rgb),
        name: selectedColour.ProductColoursMasterMain?.name || '',
      },
      {
        slot: 'trim 1',
        hex: rgbToHex(selectedColour.ProductColoursMasterTrim1?.rgb),
        name: selectedColour.ProductColoursMasterTrim1?.name || '',
      },
      {
        slot: 'trim 2',
        hex: rgbToHex(selectedColour.ProductColoursMasterTrim2?.rgb),
        name: selectedColour.ProductColoursMasterTrim2?.name || '',
      },
      {
        slot: 'trim 3',
        hex: rgbToHex(selectedColour.ProductColoursMasterTrim3?.rgb),
        name: selectedColour.ProductColoursMasterTrim3?.name || '',
      },
    ].slice(0, numberOfTemplateColours);
  }

  const handleSublimationColorChange = debounce((event, index) => {
    // 1) Get the user-selected hex
    const pickedHex = event.target.value;

    // 2) Find the nearest color name from ntc
    const [exactMatch, namedColour] = ntc.name(pickedHex);

    // 3) Update our local array
    subColours[index].hex = pickedHex;
    subColours[index].name = namedColour;

    updateSublimationColourObject();
  }, 500);

  /**
   * Build a "custom" color object based on the user’s selected subColours.
   */
  function updateSublimationColourObject() {
    // Construct a new product-colour object.
    let customColour = {
      id: null,
      displayName: 'Custom',
      pathName: 'custom',
      mens: true,
      womens: true,
      youth: true,
      images: null,
      __typename: 'Product_ProductColours',
      // For each subColour slot, fill in the correct ProductColoursMaster
      ProductColoursMasterMain: {
        id: null,
        rgb: hexToRgb(subColours[0].hex),
        name: subColours[0].name,
        __typename: 'ProductColoursMaster',
      },
      ProductColoursMasterTrim1: subColours[1]
        ? {
            id: null,
            rgb: hexToRgb(subColours[1].hex),
            name: subColours[1].name,
            __typename: 'ProductColoursMaster',
          }
        : null,
      ProductColoursMasterTrim2: subColours[2]
        ? {
            id: null,
            rgb: hexToRgb(subColours[2].hex),
            name: subColours[2].name,
            __typename: 'ProductColoursMaster',
          }
        : null,
      ProductColoursMasterTrim3: subColours[3]
        ? {
            id: null,
            rgb: hexToRgb(subColours[3].hex),
            name: subColours[3].name,
            __typename: 'ProductColoursMaster',
          }
        : null,
    };

    // Now pass it to your existing logic that updates the store
    changeColour(customColour, true);
  }

  function changeSelectedType(type) {
    const showing = $productDataStore.showing;
    if (!$productDataStore[type] && $productDataStore[type + 'sAvailable']) {
      // Navigate back to product selection page and set a hash to enable to the correct section
      // Can't set the showing value in store as we don't have the product data yet.
      goto('/design/' + $productDataStore[showing].slug + '/choose-uniform-items/#' + type);
      return;
    }
    // Here on changing type we also update the showing value in store to trigger preview update.
    switch (type) {
      case 'top':
        selectedType = 'top';
        $productDataStore.showing = 'top';
        break;
      case 'bottom':
        selectedType = 'bottom';
        $productDataStore.showing = 'bottom';
        break;
      case 'hat':
        selectedType = 'hat';
        $productDataStore.showing = 'hat';
        break;
      case 'socks':
        selectedType = 'socks';
        $productDataStore.showing = 'socks';
        break;
      case 'accessories':
        selectedType = 'accessories';
        $productDataStore.showing = 'accessories';
        break;

      default:
        break;
    }
  }

  function changeColour(colour, noAdvance = false) {
    $productDataStore[$productDataStore.showing].selectedColour = colour;

    designState.updateProductColour($konvaStageId, $productDataStore[$productDataStore.showing], colour);
    // On mobile devices we also want to advance to the next step
    // We lose the side control panel at widths less than 700px
    // TODO: We should set a flag in the store to indicate if we are on a mobile device
    if (window.innerWidth < 700 && !noAdvance) {
      goto('/design/' + $productDataStore[selectedType].slug + '/front-print');
    }
  }

  let isMounted = false;
  let subColours = [];
  let selectedType;
  onMount(() => {
    resizeFunction();
    selectedType = $productDataStore.showing;

    // Set the gender and colour based on query string if present
    const urlParams = new URLSearchParams(window.location.search);
    const colourOverride = urlParams.get('colour');
    if (colourOverride) {
      const colourOverrideObject = $productDataStore[selectedType].colours.find((c) => c.pathName === colourOverride);
      if (colourOverrideObject) {
        $productDataStore[selectedType].selectedColour = colourOverrideObject;
      }
    }
    const genderOverride = urlParams.get('gender');
    if (genderOverride) {
      $productDataStore[$productDataStore.showing].gender = genderOverride;
    }

    // TODO - should this be dynamic and update the colour inputs when the user selects a pre-defined colour option?
    // Use design state if possible as sometimes the product data store is using the default colour due to SSR
    let selectedColour = $designState[0]?.[selectedType]?.colour
      ? $designState[0][selectedType].colour
      : $productDataStore[selectedType]?.selectedColour;

    // Sublimation product colour picker
    let selectedTemplate = $productDataStore[selectedType]?.selectedSublimationTemplate;
    let numberOfTemplateColours = selectedTemplate?.SublimationTemplate?.numberOfColours;

    // Pre-fill the sublimation template colours
    subColours = generateSubColoursObject(selectedColour, numberOfTemplateColours);

    isMounted = true;
  });
</script>

{#if isMounted}
  <div class="heading">
    <h2>{$productDataStore[selectedType].name}</h2>
  </div>

  <div class="content">
    <ProductTypeSelectionColour {selectedType} clickFn={changeSelectedType} />

    <div class="options">
      {#if $productDataStore[selectedType].isSublimationProduct}
        <div class="span-two-columns">
          <div class="heading">Enter Custom Colours</div>
          <div class="colour-inputs">
            {#each subColours as sub, i}
              <div class="picker-wrapper">
                <div class="cp-wrapper">
                  <input type="color" bind:value={sub.hex} on:input={(e) => handleSublimationColorChange(e, i)} />
                </div>
                <div class="picker-label">
                  {sub.slot} <span class="colour-name">({sub.name})</span>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}
      {#if $productDataStore[selectedType].isSublimationProduct}
        <div class="span-two-columns">
          <div class="heading">Select From Popular Colours</div>
        </div>
      {/if}
      {#if $productDataStore[selectedType]?.colours?.length}
        {#each $productDataStore[selectedType].colours as colour (colour.id)}
          <ColourOptionCard
            {colour}
            productRootSlug={$productDataStore[selectedType].rootSlug}
            selected={colour.id === $productDataStore[selectedType].selectedColour.id}
            gender={$productDataStore[$productDataStore.showing].gender}
            side={$productDataStore.side}
            clickFn={changeColour}
          />
        {/each}
      {:else}
        <div class="no-colours">
          No Colours Found for {$productDataStore[selectedType].name}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .heading {
    text-transform: capitalize;
    text-align: center;
    font-size: 1.4rem;
    padding-top: 0.5rem;
  }
  .content {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8rem;
    padding: 2rem;
    padding-bottom: 60px;
  }

  .options {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2rem;
    height: fit-content;
  }

  .span-two-columns {
    grid-column: 1 / span 2;
  }

  .heading {
    text-transform: capitalize;
    text-align: center;
    font-size: 1.2rem;
    padding: 1rem;
    background-color: #f5f5f5;
    border-radius: 5px;
  }

  .colour-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  input[type='color'] {
    padding: 0;
    width: 150%;
    height: 150%;
    margin: -25%;
    cursor: pointer;
  }

  .picker-wrapper {
    margin-top: 1rem;
    display: flex;
    align-items: center;
  }

  .cp-wrapper {
    overflow: hidden;
    width: 2em;
    height: 2em;
    /* optional formatting below here */
    border-radius: 50%;
    box-shadow: 1px 1px 3px 0px grey;
    cursor: pointer;
  }

  .picker-label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-transform: capitalize;
    font-size: 1.4rem;
    color: #4a4848;
    font-weight: 600;
    margin-left: 1rem;
  }

  .colour-name {
    margin-top: 0.1rem;
    font-size: 1rem;
    color: #8f8d8d;
  }

  @media screen and (min-width: 600px) {
    .content {
      grid-template-columns: minmax(0, 80px) minmax(0, 1fr);
      gap: 2rem;
    }
  }
</style>
